import React, { useState } from 'react';

interface Folder {
  id: string;
  name: string;
  webViewLink?: string;
  modifiedTime?: string;
  shared?: boolean;
  children?: Folder[];
}

interface FolderPickerProps {
  reason: string;
  folders: Folder[];
  searchContext?: string;
  onSelectFolder: (folderId: string, folderName: string) => void;
}

const FolderPicker: React.FC<FolderPickerProps> = ({
  reason,
  folders,
  searchContext,
  onSelectFolder,
}) => {
  console.log('[FolderPicker Component] 🚀 Rendering FolderPicker component');
  console.log('[FolderPicker Component] Reason:', reason);
  console.log('[FolderPicker Component] Folders array:', folders);
  console.log('[FolderPicker Component] Folders count:', folders?.length);
  console.log('[FolderPicker Component] Search context:', searchContext);

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const renderFolder = (folder: Folder, depth: number = 0): React.ReactElement => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);

    return (
      <div key={folder.id} style={{ marginLeft: `${depth * 20}px` }}>
        <div
          style={{
            ...styles.folderItem,
            paddingLeft: `${hasChildren ? 0 : 20}px`,
          }}
        >
          {hasChildren && (
            <button
              onClick={() => toggleFolder(folder.id)}
              style={styles.expandButton}
              aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          <button
            onClick={() => onSelectFolder(folder.id, folder.name)}
            style={styles.folderButton}
          >
            <span style={styles.folderIcon}>📁</span>
            <span style={styles.folderName}>{folder.name}</span>
            {folder.shared && (
              <span style={styles.sharedBadge}>Shared</span>
            )}
          </button>
        </div>

        {hasChildren && isExpanded && (
          <div style={styles.childrenContainer}>
            {folder.children!.map(child => renderFolder(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Debug render
  console.log('[FolderPicker Component] About to render with folders:', folders);

  if (!folders) {
    console.error('[FolderPicker Component] ❌ ERROR: folders prop is null or undefined!');
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.icon}>📂</div>
        <div style={styles.content}>
          <div style={styles.title}>Select a Folder</div>
          <div style={styles.reason}>{reason}</div>
          {searchContext && (
            <div style={styles.searchContext}>
              Searching for: <strong>{searchContext}</strong>
            </div>
          )}
        </div>
      </div>

      <div style={styles.folderList}>
        {folders && folders.length > 0 ? (
          folders.map(folder => renderFolder(folder))
        ) : (
          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--fg-2)' }}>
            {!folders ? (
              <>❌ Error: No folder data received. Please try again.</>
            ) : (
              <>No folders available. You may not have access to any Drive folders.</>
            )}
          </div>
        )}
      </div>

      <div style={styles.footer}>
        <span style={styles.footerIcon}>💡</span>
        <span style={styles.footerText}>
          Click any folder to search within it
        </span>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'rgba(59, 130, 246, 0.05)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    borderRadius: '12px',
    padding: '1.25rem',
    marginTop: '1rem',
    maxWidth: '600px',
  },
  header: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    alignItems: 'flex-start',
  },
  icon: {
    fontSize: '2rem',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--fg-1)',
    marginBottom: '0.5rem',
  },
  reason: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: 'var(--fg-2)',
    marginBottom: '0.5rem',
  },
  searchContext: {
    fontSize: '0.8125rem',
    color: 'var(--fg-3)',
    fontStyle: 'italic',
  },
  folderList: {
    backgroundColor: 'var(--bg-surface-1)',
    borderRadius: '8px',
    padding: '0.75rem',
    maxHeight: '400px',
    overflowY: 'auto' as const,
    border: '1px solid var(--border-subtle)',
  },
  folderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    marginBottom: '0.25rem',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    fontSize: '0.75rem',
    color: 'var(--fg-3)',
    width: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s ease',
    fontFamily: 'var(--font-sans)',
  },
  folderButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: 'var(--fg-1)',
    transition: 'background-color 0.2s ease',
    flex: 1,
    textAlign: 'left' as const,
    fontFamily: 'var(--font-sans)',
  },
  folderIcon: {
    fontSize: '1.125rem',
    flexShrink: 0,
  },
  folderName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  sharedBadge: {
    fontSize: '0.75rem',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: 'rgb(59, 130, 246)',
    fontWeight: 500,
  },
  childrenContainer: {
    marginLeft: '0.5rem',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: 'rgba(59, 130, 246, 0.05)',
    borderRadius: '6px',
  },
  footerIcon: {
    fontSize: '1rem',
  },
  footerText: {
    fontSize: '0.8125rem',
    color: 'var(--fg-2)',
  },
};

export default FolderPicker;
